import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
  Self,
} from '@angular/core';
import { InputType } from '@shared/components/input/root/input-type';
import { InputRootController } from '@shared/components/input/root/input-root-controller.directive';
import { FormControlName, NgModel } from '@angular/forms';

@Directive({
  selector: '[appInput]',
  providers: [{ provide: InputType, useExisting: InputDirective }],
})
export class InputDirective extends InputRootController implements AfterViewInit, OnDestroy {
  @HostBinding('attr.type')
  @Input() public type: string = 'text';

  @HostBinding('attr.autocomplete')
  @Input() public autocomplete: string = 'off';

  @HostBinding('attr.placeholder')
  @Input() public placeholder: string = '';

  @Input() public trim: boolean = true;

  /** Allow autofocus via boolean binding or bare attribute */
  private _autofocus = false;
  @Input()
  set autofocus(v: boolean | string | null | undefined) {
    this._autofocus = v === '' || v === true || v === 'true';
  }

  /** Expose attribute for selectors; not used for behavior */
  @HostBinding('attr.autofocus')
  get hostAutofocusAttr(): '' | null {
    return this._autofocus ? '' : null;
  }

  /** Text on the right side badge */
  @Input() public append?: string;

  /** Text on the left side badge */
  @Input() public prepend?: string;

  /** Click events */
  @Output() public appendClick = new EventEmitter<{ label: string }>();
  @Output() public prependClick = new EventEmitter<{ label: string }>();

  /** Trim value on blur */
  @HostListener('blur')
  public onBlur(): void {
    this.trimValue()
  }

  @HostListener('keydown.enter', ['$event'])
  public onEnter(event: Event): void {
    this.trimValue()
  }

  /** Separate refs & listener disposers */
  private appendEl?: HTMLElement;
  private prependEl?: HTMLElement;
  private removeAppendListener?: () => void;
  private removePrependListener?: () => void;

  private observer?: MutationObserver;

  constructor(
    @Optional() @Self() @Inject(NgModel) protected override ngModel: NgModel,
    @Optional() @Self() @Inject(FormControlName)
    protected override formControl: FormControlName,
    protected override cdr: ChangeDetectorRef,
    protected override elementRef: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2
  ) {
    super(ngModel, formControl, cdr, elementRef);
  }

  public ngAfterViewInit(): void {
    if (this.append || this.prepend || this._autofocus) {
      this.setupObserver();
    }
  }

  public ngOnDestroy(): void {
    this.teardownLabels();
    this.observer?.disconnect();
  }

  /** Trims value **/
  private trimValue(): void {
    if (!this.trim) return;
    const value = this.elementRef.nativeElement.value;
    if (value) {
      const trimmed = value.trim();
      if (value !== trimmed) {
        this.elementRef.nativeElement.value = trimmed;

        if (this.ngModel) {
          this.ngModel.control.setValue(trimmed);
        }
        if (this.formControl) {
          this.formControl.control.setValue(trimmed);
        }
      }
    }
  }

  private setupObserver(): void {
    this.observer = new MutationObserver(() => {
      if (this.isVisible()) {
        if (this.append || this.prepend) {
          this.setupLabels();
        }
        this.focusIfNeeded();
        this.observer?.disconnect();
      }
    });

    this.observer.observe(document.body, { childList: true, subtree: true });

    if (this.isVisible()) {
      if (this.append || this.prepend) {
        this.setupLabels();
      }
      this.focusIfNeeded();
      this.observer?.disconnect();
    }
  }

  private isVisible(): boolean {
    return this.elementRef.nativeElement.offsetParent !== null;
  }

  private focusIfNeeded(): void {
    if (!this._autofocus) return;

    const el = this.elementRef.nativeElement;
    // avoid focusing disabled/readonly fields
    if (el.disabled || el.readOnly) return;

    // defer to next frame to ensure layout (and padding) is applied
    requestAnimationFrame(() => {
      // double-check visibility right before focusing
      if (this.isVisible()) {
        el.focus({ preventScroll: true });
      }
    });
  }

  private setupLabels(): void {
    if (this.append) {
      this.appendEl = this.addLabel(this.append, true);
      this.removeAppendListener = this.renderer.listen(
        this.appendEl,
        'click',
        () => this.appendClick.emit({ label: this.append! })
      );
    }

    if (this.prepend) {
      this.prependEl = this.addLabel(this.prepend, false);
      this.removePrependListener = this.renderer.listen(
        this.prependEl,
        'click',
        () => this.prependClick.emit({ label: this.prepend! })
      );
    }
  }

  private teardownLabels(): void {
    const parent = this.elementRef.nativeElement.parentElement;
    if (this.removeAppendListener) { this.removeAppendListener(); this.removeAppendListener = undefined; }
    if (this.removePrependListener) { this.removePrependListener(); this.removePrependListener = undefined; }

    if (this.appendEl && parent) { this.renderer.removeChild(parent, this.appendEl); }
    if (this.prependEl && parent) { this.renderer.removeChild(parent, this.prependEl); }

    this.appendEl = undefined;
    this.prependEl = undefined;

    // reset padding if we removed labels
    this.elementRef.nativeElement.style.paddingLeft = '';
    this.elementRef.nativeElement.style.paddingRight = '';
  }

  private addLabel(label: string, append: boolean = true): HTMLElement {
    const el = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(el, 'absolute');
    this.renderer.addClass(el, 'top-0');

    if (append) {
      this.renderer.addClass(el, 'right-0');
      this.renderer.addClass(el, 'rounded-r');
    } else {
      this.renderer.addClass(el, 'left-0');
      this.renderer.addClass(el, 'rounded-l');
    }

    this.renderer.addClass(el, 'bg-gray-100');
    this.renderer.addClass(el, 'dark:bg-dark-hover');
    this.renderer.addClass(el, 'border');
    this.renderer.addClass(el, 'border-slate-300');
    this.renderer.addClass(el, 'dark:border-dark-border');
    this.renderer.addClass(el, 'text-sm');
    this.renderer.addClass(el, 'flex');
    this.renderer.addClass(el, 'justify-center');
    this.renderer.addClass(el, 'p-2');
    this.renderer.setStyle(el, 'cursor', 'pointer');
    this.renderer.setProperty(el, 'textContent', label);

    this.elementRef.nativeElement.parentElement?.appendChild(el);

    // adjust padding after render
    requestAnimationFrame(() => {
      const width = el.offsetWidth;
      this.addSpacing(width, append);
    });

    return el;
  }

  private addSpacing(width: number, append: boolean = true): void {
    const px = `${width + 4}px`;
    if (append) {
      this.elementRef.nativeElement.style.paddingRight = px;
    } else {
      this.elementRef.nativeElement.style.paddingLeft = px;
    }
  }
}
